import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  incCampaignIdViewCount,
  removeCampaignIdViewsData,
  setCampaignIdExpirationDate,
  setCampaignHideDuration,
  setUniqueViews,
} from "store/slices/backgroundSlice";
import {
  selectCampaignHideDuration,
  selectCampaignIdViewCounts,
  selectCampaignUniqueViews,
} from "store/selectors/backgroundSelectors";
import { useContent } from "contexts/contentContext/contentContext";
import useBackground from "./useBackground";

/**
 * 1. If fetched background is from campaign, get campaign id and max view counts from response.
 * 2. If current stored view count for this campaign is less than max view count, increment view count.
 * 3. If current stored view count for this campaign is equal to max view count, set expiration date for this campaign.
 *    It means from now user will not see this campaign ad until expiration date will not be expired.
 * 4. All muted campaigns (exp. date of which is more then current date) will be added to payload during fetching new ad background.
 * 5. On page load, we check if there are campaigns need to be shown again and remove their data from storage.
 * 6. If during the fetching new ad background we get muted campaign, we refetch default background image.
 */
const useCampaignViewCounts = (location?: "NEWHOMEPAGE" | "DOWNLOAD") => {
  const dispatch = useAppDispatch();

  const { getAdsBackground, fetchDefaultBackgroundImage } = useContent();
  const { getCampaignIdsForExcluding } = useBackground();
  const campaignsViewCounts = useAppSelector(selectCampaignIdViewCounts);

  const incrementCampaignViewCount = (campaignId: number) => {
    dispatch(incCampaignIdViewCount(campaignId));
  };

  const campaignId = getAdsBackground()?.campaignId;
  const campaignCurrViewCount = campaignId
    ? campaignsViewCounts?.[campaignId]?.amount || 0
    : 0;
  const campaignIdExpirationDate =
    campaignsViewCounts?.[campaignId]?.expirationDate;

  const campaignHideDuration = getAdsBackground()?.campaignHideDuration;
  const maxCampaignViewCount = getAdsBackground()?.uniqueViews;

  const storedCampaignIdHideDuration = useAppSelector(
    selectCampaignHideDuration(campaignId)
  );
  const storedCampaignUniqueViews = useAppSelector(
    selectCampaignUniqueViews(campaignId)
  );

  useEffect(() => {
    if (campaignHideDuration && storedCampaignIdHideDuration) {
      if (campaignHideDuration !== storedCampaignIdHideDuration) {
        dispatch(
          setCampaignHideDuration({
            campaignId,
            duration: campaignHideDuration,
          })
        );
        dispatch(setCampaignIdExpirationDate({ campaignId }));
      }
    }
    if (
      maxCampaignViewCount &&
      storedCampaignUniqueViews &&
      maxCampaignViewCount !== storedCampaignUniqueViews
    ) {
      dispatch(
        setUniqueViews({
          campaignId,
          uniqueViews: maxCampaignViewCount,
        })
      );
      dispatch(setCampaignIdExpirationDate({ campaignId }));
    }
  }, [
    storedCampaignIdHideDuration,
    storedCampaignUniqueViews,
    campaignHideDuration,
    maxCampaignViewCount,
    dispatch,
    campaignId,
  ]);

  // We are getting campaign id when fetching background.
  // If fetched background has campaign id, it means user sees ad.
  // Therefore, we increment view count for this campaign.
  useEffect(() => {
    if (campaignId) {
      dispatch(
        setUniqueViews({
          campaignId,
          uniqueViews: maxCampaignViewCount,
        })
      );
      dispatch(
        setCampaignHideDuration({
          campaignId,
          duration: campaignHideDuration,
        })
      );

      if (campaignCurrViewCount < maxCampaignViewCount) {
        incrementCampaignViewCount(campaignId);

        // If expiration date wasn't set yet
      } else if (!campaignIdExpirationDate) {
        // If user has seen ad more than allowed, we set expiration date for this campaign
        // which will indicate when user will be able to see this ad again.
        const expirationDate =
          Date.now() + (campaignHideDuration * 60 * 60 * 1000 || 0);
        // const testExpirationDate = Date.now() + 1000 * 60;

        dispatch(
          setCampaignIdExpirationDate({
            campaignId,
            expirationDate,
          })
        );

        // Refetch default background image
        fetchDefaultBackgroundImage({
          location: location || _autodetectLocation(),
          campaignIds: [...getCampaignIdsForExcluding(), +campaignId],
        });
      }
    }
  }, [campaignId]);

  const _clearExpiredCampaignsExpirationDate = () => {
    if (!campaignsViewCounts) return;

    const now = Date.now();

    Object.entries(campaignsViewCounts).forEach(
      ([campaignId, { expirationDate }]) => {
        if (expirationDate && expirationDate < now) {
          // Remove campaign id views data if expiration date is expired
          // so user will be able to see this ad again
          dispatch(removeCampaignIdViewsData(+campaignId));
        }
      }
    );
  };

  // On page load, we check if there are any expired campaigns
  // and remove them from storage
  useEffect(() => {
    _clearExpiredCampaignsExpirationDate();
  }, []);
};

const _autodetectLocation = () => {
  if (window.location.pathname.includes("home")) {
    return "NEWHOMEPAGE";
  } else {
    return "DOWNLOAD";
  }
};

export default useCampaignViewCounts;
